// StatCard — a key / value / meta reconciliation card.
//
// Used across the P&L summary and import dry-run screens. The value is rendered
// in Geist Mono tabular-nums (it is usually an exact money string or a count);
// `tone` tints the value. `meta` carries the small caption (e.g. "Reconciled to
// seed"). Pass a node to `value` for non-money values (counts) if preferred.

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { type MoneyTone } from "./MoneyCell";

const valueToneClass: Record<MoneyTone, string> = {
  default: "text-nx-text",
  credit: "text-nx-credit",
  debit: "text-nx-debit",
  profit: "text-nx-profit",
  muted: "text-nx-text-3",
};

export function StatCard({
  label,
  value,
  meta,
  tone = "default",
  icon,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  meta?: React.ReactNode;
  tone?: MoneyTone;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("gap-1 p-4", className)}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
          {label}
        </span>
        {icon && <span className="text-nx-text-3">{icon}</span>}
      </div>
      <div
        className={cn(
          "font-mono text-xl leading-tight font-semibold tabular-nums",
          valueToneClass[tone],
        )}
      >
        {value}
      </div>
      {meta && <div className="text-xs text-nx-text-3">{meta}</div>}
    </Card>
  );
}
